/**
 * SectionBlock — renders a single newsletter section inside the canvas.
 *
 * Supported types:
 *   Legacy:    text, image, hero, cta, divider, spacer, columns
 *   New:       header, founders_focus, meet_engineer, appreciation, project_update, footer
 */
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Copy } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { useEditorStore } from '../stores/editorStore';
import { sanitizeHtml } from '../lib/sanitize';

// New section type components (v2 — plain text founders focus)
import { HeaderSection } from '../sections/HeaderSection';
import { FoundersFocusSection } from '../sections/FoundersFocusSection';
import { MeetEngineerSection } from '../sections/MeetEngineerSection';
import { AppreciationSection } from '../sections/AppreciationSection';
import { ProjectUpdateSection } from '../sections/ProjectUpdateSection';
import { FooterSection } from '../sections/FooterSection';
import { ImageSection } from '../sections/ImageSection';

// Human-readable type labels for the action bar
const TYPE_LABELS = {
  text: 'Text',
  image: 'Image',
  hero: 'Hero',
  cta: 'CTA',
  divider: 'Divider',
  spacer: 'Spacer',
  columns: 'Two Columns',
  header: 'Header',
  footer: 'Footer',
  founders_focus: "Founder's Focus",
  meet_engineer: 'Meet the Engineer',
  appreciation: 'Appreciation',
  project_update: 'Project Update',
};

export function SectionBlock({ section, isSelected, readOnly = false }) {
  const { selectSection, updateSection, removeSection, duplicateSection } = useEditorStore();

  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    cursor: 'default',
  };

  const st = section.settings || {};
  const contentStyle = {
    padding: st.padding || '16px',
    backgroundColor: st.backgroundColor || '#ffffff',
    color: st.textColor || '#111827',
    fontSize: st.fontSize || '16px',
    textAlign: st.textAlign || 'left',
    borderRadius: st.borderRadius || '0px',
  };

  const handleHtmlChange = (html) => updateSection(section.id, { html, content: html });

  // ── New section types rendered by dedicated components ──────────────────────
  const NEW_TYPES = ['header', 'founders_focus', 'meet_engineer', 'appreciation', 'project_update', 'footer', 'image'];

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={(e) => { e.stopPropagation(); if (!readOnly) selectSection(section.id); }}
    >
      {/* Selection ring — inset shadow takes zero layout space, no gap between sections */}
      <div style={{
        position: 'relative',
        boxShadow: isSelected ? 'inset 0 0 0 2px #f54b01' : 'none',
        transition: 'box-shadow 0.15s',
      }}>

        {/* Action bar (only when selected and not readOnly) */}
        {isSelected && !readOnly && (
          <div style={{
            position: 'absolute', top: -32, left: 0, right: 0,
            display: 'flex', alignItems: 'center', gap: 4,
            background: '#f54b01', borderRadius: '6px 6px 0 0',
            padding: '2px 8px', zIndex: 10, height: 30,
          }}>
            <button
              {...attributes} {...listeners}
              style={{ background: 'none', border: 'none', cursor: 'grab', color: '#fff', padding: 0, display: 'flex' }}
              title="Drag to reorder"
            >
              <GripVertical size={16} />
            </button>
            <span style={{
              flex: 1, fontSize: 11, color: '#fff', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: 1,
            }}>
              {TYPE_LABELS[section.type] || section.type}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); duplicateSection(section.id); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: '0 4px', display: 'flex' }}
              title="Duplicate"
            >
              <Copy size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: '0 4px', display: 'flex' }}
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {/* ── Section content ──────────────────────────────────────────────── */}

        {/* New dedicated-component types */}
        {NEW_TYPES.includes(section.type) && (() => {
          const props = { section, isSelected: isSelected && !readOnly, updateSection };
          switch (section.type) {
            case 'header': return <HeaderSection        {...props} />;
            case 'founders_focus': return <FoundersFocusSection {...props} />;
            case 'meet_engineer': return <MeetEngineerSection  {...props} />;
            case 'appreciation': return <AppreciationSection  {...props} />;
            case 'project_update': return <ProjectUpdateSection {...props} />;
            case 'footer': return <FooterSection        {...props} />;
            case 'image': return <ImageSection         {...props} />;
            default: return null;
          }
        })()}

        {/* Legacy inline types */}
        {!NEW_TYPES.includes(section.type) && (
          <div style={contentStyle}>

            {section.type === 'divider' && (
              <hr style={{ border: 'none', borderTop: `2px solid ${st.textColor || 'var(--em-border)'}`, margin: '8px 0' }} />
            )}

            {section.type === 'spacer' && (
              <div style={{ height: st.padding || '32px', background: 'transparent' }} />
            )}

            {(section.type === 'text' || !section.type) && (
              isSelected && !readOnly
                ? <RichTextEditor
                  content={section.html || section.content || ''}
                  onChange={handleHtmlChange}
                  placeholder="Start typing your content…"
                />
                : <div
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.html || section.content || '<p><em>Empty section — click to edit</em></p>') }}
                  style={{ minHeight: 40, lineHeight: 1.6 }}
                />
            )}


            {section.type === 'hero' && (
              <div style={{
                background: st.backgroundColor || 'linear-gradient(135deg,#f54b01,#ff8c42)',
                padding: '48px 32px',
                textAlign: 'center',
                borderRadius: st.borderRadius || 0,
              }}>
                {isSelected && !readOnly
                  ? <RichTextEditor
                    content={section.html || section.content || '<h1>Hero Title</h1><p>Subtitle goes here</p>'}
                    onChange={handleHtmlChange}
                  />
                  : <div
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.html || section.content || '<h1 style="color:#fff">Hero Title</h1><p style="color:#fff">Subtitle goes here</p>') }}
                    style={{ color: st.textColor || '#fff' }}
                  />
                }
              </div>
            )}

            {section.type === 'cta' && (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                {isSelected && !readOnly
                  ? <RichTextEditor
                    content={section.html || section.content || '<p>Call to action text</p>'}
                    onChange={handleHtmlChange}
                  />
                  : <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.html || section.content || '<p>Call to action text</p>') }} />
                }
                <a
                  href={section.ctaUrl || '#'}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block', marginTop: 16,
                    background: st.accentColor || '#f54b01',
                    color: '#fff', padding: '12px 32px', borderRadius: 6,
                    textDecoration: 'none', fontWeight: 600, fontSize: 15,
                  }}
                >
                  {section.ctaLabel || 'Learn More'}
                </a>
              </div>
            )}

            {section.type === 'columns' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[0, 1].map((col) => (
                  <div key={col} style={{ border: '1px dashed #d1d5db', borderRadius: 4, padding: 8 }}>
                    {isSelected && !readOnly
                      ? <RichTextEditor
                        content={(section.columns || [])[col]?.html || ''}
                        onChange={(html) => {
                          const cols = [...(section.columns || [{}, {}])];
                          cols[col] = { ...cols[col], html };
                          updateSection(section.id, { columns: cols });
                        }}
                      />
                      : <div dangerouslySetInnerHTML={{ __html: sanitizeHtml((section.columns || [])[col]?.html || `<p>Column ${col + 1}</p>`) }} />
                    }
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
}
